import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FaImages, FaRedo, FaUpload } from "react-icons/fa";
import {
  AUTH_FOUNDER_HERO_SETTING_KEY,
  DEFAULT_AUTH_FOUNDER_HERO_PATH,
  fetchAuthFounderHeroSetting,
  getDefaultAuthFounderHeroUrl,
  resolveAuthFounderHeroUrl,
  saveAuthFounderHeroImageUrl,
  uploadAuthFounderHeroImage,
} from "../../services/authFounderHero.service";
import { platformManagementExtractMessage } from "../../services/platformManagement.service";
import "../../assets/styles/master_category_admin.scss";
import "../../assets/styles/super_admin.scss";
import "../../assets/styles/super_admin_auth_hero.scss";

const ALLOWED_EXT = new Set(["jpg", "jpeg", "png", "webp"]);
const MAX_BYTES = 800 * 1024;

export default function SuperAdminAuthHeroManagementPage() {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [setting, setSetting] = useState(null);
  const [draftPath, setDraftPath] = useState(DEFAULT_AUTH_FOUNDER_HERO_PATH);
  const [previewUrl, setPreviewUrl] = useState(getDefaultAuthFounderHeroUrl);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const row = await fetchAuthFounderHeroSetting();
      setSetting(row);
      const path = String(row?.settingValue || DEFAULT_AUTH_FOUNDER_HERO_PATH).trim();
      setDraftPath(path || DEFAULT_AUTH_FOUNDER_HERO_PATH);
      setPreviewUrl(resolveAuthFounderHeroUrl(path || DEFAULT_AUTH_FOUNDER_HERO_PATH));
    } catch (err) {
      toast.error(platformManagementExtractMessage(err, "Could not load auth hero image setting."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const validateFile = (file) => {
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    if (!ALLOWED_EXT.has(ext)) {
      toast.error("Only JPG, JPEG, PNG and WEBP images are allowed.");
      return false;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Hero image must be 800 KB or smaller.");
      return false;
    }
    return true;
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !validateFile(file)) return;

    const blobPreview = URL.createObjectURL(file);
    setPreviewUrl(blobPreview);
    setUploading(true);

    try {
      const imagePath = await uploadAuthFounderHeroImage(file);
      const updated = await saveAuthFounderHeroImageUrl(imagePath);
      setSetting(updated);
      const savedPath = String(updated?.settingValue || imagePath).trim();
      setDraftPath(savedPath);
      setPreviewUrl(resolveAuthFounderHeroUrl(savedPath));
      toast.success("Auth hero image updated for login, signup, and super admin login.");
    } catch (err) {
      setPreviewUrl(resolveAuthFounderHeroUrl(draftPath));
      toast.error(platformManagementExtractMessage(err, "Hero image upload failed."));
    } finally {
      URL.revokeObjectURL(blobPreview);
      setUploading(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const updated = await saveAuthFounderHeroImageUrl(DEFAULT_AUTH_FOUNDER_HERO_PATH);
      setSetting(updated);
      const savedPath = String(updated?.settingValue || DEFAULT_AUTH_FOUNDER_HERO_PATH).trim();
      setDraftPath(savedPath);
      setPreviewUrl(resolveAuthFounderHeroUrl(savedPath));
      toast.success("Auth hero image reset to default.");
    } catch (err) {
      toast.error(platformManagementExtractMessage(err, "Could not reset auth hero image."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mca-page super-admin-page sa-auth-hero-page">
      <nav className="mca-breadcrumb" aria-label="Breadcrumb">
        <ol className="mca-breadcrumb__list">
          <li>
            <Link to="/super-admin/dashboard">Super Admin</Link>
          </li>
          <li aria-current="page">Auth Hero Image</li>
        </ol>
      </nav>

      <header className="mca-hero">
        <div>
          <div className="mca-hero__title-row">
            <h1 className="mca-hero__title">Auth Hero Image</h1>
            <span className="mca-hero__badge">Shared</span>
          </div>
          <p className="mca-hero__subtitle">
            One background image for user login, sign up, and super admin login left panels.
          </p>
        </div>
      </header>

      <section className="mca-card sa-auth-hero-page__card">
        <div className="sa-auth-hero-page__intro">
          <span className="sa-auth-hero-page__intro-icon" aria-hidden>
            <FaImages />
          </span>
          <div>
            <h2 className="mca-section-title">Founder access background</h2>
            <p className="sa-auth-hero-page__copy">
              Upload a wide logistics or trade image. Recommended landscape orientation. Max 800 KB
              (JPG, PNG, WEBP).
            </p>
          </div>
        </div>

        {loading ? (
          <p className="sa-auth-hero-page__status">Loading current image…</p>
        ) : (
          <>
            <div className="sa-auth-hero-page__preview" aria-label="Auth page left panel preview">
              <div
                className="sa-auth-hero-page__preview-image"
                style={{ backgroundImage: `url(${previewUrl})` }}
              />
              <div className="sa-auth-hero-page__preview-overlay" aria-hidden />
              <div className="sa-auth-hero-page__preview-caption">
                Auth page left panel preview
              </div>
            </div>

            <div className="sa-auth-hero-page__meta">
              <strong>Setting key:</strong> {AUTH_FOUNDER_HERO_SETTING_KEY}
              <span className="sa-auth-hero-page__path">{draftPath}</span>
            </div>

            <div className="sa-auth-hero-page__actions">
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                className="sa-auth-hero-page__file-input"
                onChange={handleFileChange}
              />
              <button
                type="button"
                className="mca-btn mca-btn--secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || saving}
              >
                <FaUpload aria-hidden />
                {uploading ? "Uploading…" : "Upload image"}
              </button>
              <button
                type="button"
                className="mca-btn mca-btn--secondary"
                onClick={handleReset}
                disabled={uploading || saving}
              >
                <FaRedo aria-hidden />
                {saving ? "Resetting…" : "Reset to default"}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
